import React, { useState, useEffect, useRef } from 'react';
import StockSearch from './components/StockSearch';
import StockQuote from './components/StockQuote';
import StockChart from './components/StockChart';
import Portfolio from './components/Portfolio';
import StockAnalysis from './components/StockAnalysis';
import AIAnalysis from './components/AIAnalysis';
import IntradayChart from './components/IntradayChart';
import StockRecommendation from './components/StockRecommendation';
import Login from './components/Login';
import AdminPage from './components/AdminPage';
import LanguageSelector from './components/LanguageSelector';
import { LanguageProvider, useLanguage } from './contexts/LanguageContext';
import { stockService } from './services/stockService';
import { authService } from './services/authService';
import { BarChart3, RefreshCw, AlertCircle, LogOut, ChevronUp, ChevronDown } from 'lucide-react';
import './App.css';

function AppContent() {
  const { t } = useLanguage();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [authLoading, setAuthLoading] = useState(true);
  const [loginError, setLoginError] = useState(null);
  const [user, setUser] = useState(null);
  const [currentPage, setCurrentPage] = useState('stock'); // 'stock' or 'admin'
  const [selectedStock, setSelectedStock] = useState(null);
  const [watchlist, setWatchlist] = useState([]);
  const [selectedStockIndex, setSelectedStockIndex] = useState(-1);
  const [quote, setQuote] = useState(null);
  const [historicalData, setHistoricalData] = useState([]);
  const [currentDateRange, setCurrentDateRange] = useState('1y');
  const [loading, setLoading] = useState({
    quote: false,
    history: false
  });
  const [error, setError] = useState({
    quote: null,
    history: null
  });
  const [apiStatus, setApiStatus] = useState('checking');

  // Ref for stock details area to enable scrolling
  const stockDetailsRef = useRef(null);

  // Function to scroll to stock details area
  const scrollToStockDetails = () => {
    // Only scroll on iPad and smaller screens where sidebar is above content
    if (window.innerWidth <= 1023 && stockDetailsRef.current) {
      setTimeout(() => {
        stockDetailsRef.current.scrollIntoView({
          behavior: 'smooth',
          block: 'start',
          inline: 'nearest'
        });
      }, 100); // Small delay to ensure content is rendered
    }
  };

  useEffect(() => {
    checkAuthStatus();
    
    // Register service worker for PWA functionality
    if ('serviceWorker' in navigator && process.env.NODE_ENV === 'production') {
      window.addEventListener('load', () => {
        navigator.serviceWorker.register('/sw.js')
          .then((registration) => {
            console.log('SW registered: ', registration);
          })
          .catch((registrationError) => {
            console.log('SW registration failed: ', registrationError);
          });
      });
    }
  }, []);

  useEffect(() => {
    if (isAuthenticated) {
      checkApiHealth();
    }
  }, [isAuthenticated]);

  const checkAuthStatus = async () => {
    try {
      const authData = await authService.checkAuthStatus();
      if (authData.authenticated) {
        setIsAuthenticated(true);
        setUser({ 
          username: authData.username,
          role: authData.role 
        });
      }
    } catch (error) {
      console.error('Auth check failed:', error);
    } finally {
      setAuthLoading(false);
    }
  };

  const handleLogin = async (credentials) => {
    try {
      setLoginError(null);
      const result = await authService.login(credentials);
      setIsAuthenticated(true);
      setUser({ 
        username: result.username,
        role: result.role 
      });
    } catch (error) {
      // Pass the enhanced error object with additional context
      const errorInfo = {
        error: error.message,
        field: error.field,
        canRetry: error.canRetry,
        statusCode: error.statusCode
      };
      setLoginError(errorInfo);
      throw error;
    }
  };

  const handleLogout = async () => {
    try {
      await authService.logout();
      setIsAuthenticated(false);
      setUser(null);
      setSelectedStock(null);
      setQuote(null);
      setHistoricalData([]);
      setApiStatus('checking');
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  const checkApiHealth = async () => {
    try {
      await stockService.checkHealth();
      setApiStatus('healthy');
    } catch (error) {
      setApiStatus('unhealthy');
      console.error('API health check failed:', error);
    }
  };

  const handleStockSelect = async (stock, dateRange = '1y') => {
    setSelectedStock(stock);
    setCurrentDateRange(dateRange);
    setError({ quote: null, history: null });

    // Scroll to stock details area
    scrollToStockDetails();

    // Load quote
    setLoading(prev => ({ ...prev, quote: true }));
    try {
      const quoteData = await stockService.getQuote(stock.symbol);
      setQuote(quoteData);
    } catch (error) {
      setError(prev => ({ ...prev, quote: error.message }));
    } finally {
      setLoading(prev => ({ ...prev, quote: false }));
    }

    // Load historical data with date range
    setLoading(prev => ({ ...prev, history: true }));
    try {
      const historyData = await stockService.getHistoricalData(stock.symbol, 'daily', dateRange);
      setHistoricalData(historyData);
    } catch (error) {
      setError(prev => ({ ...prev, history: error.message }));
    } finally {
      setLoading(prev => ({ ...prev, history: false }));
    }
  };

  const handleDateRangeChange = async (newRange) => {
    if (!selectedStock) return;
    
    setCurrentDateRange(newRange);
    setError(prev => ({ ...prev, history: null }));
    
    // Load historical data with new date range
    setLoading(prev => ({ ...prev, history: true }));
    try {
      const historyData = await stockService.getHistoricalData(selectedStock.symbol, 'daily', newRange);
      setHistoricalData(historyData);
    } catch (error) {
      setError(prev => ({ ...prev, history: error.message }));
    } finally {
      setLoading(prev => ({ ...prev, history: false }));
    }
  };

  const refreshData = () => {
    if (selectedStock) {
      handleStockSelect(selectedStock, currentDateRange);
    }
  };

  // Mobile navigation functions
  const navigateToNextStock = () => {
    if (watchlist.length === 0) return;
    
    const nextIndex = selectedStockIndex < watchlist.length - 1 ? selectedStockIndex + 1 : 0;
    const nextStock = watchlist[nextIndex];
    setSelectedStockIndex(nextIndex);
    handleStockSelect(nextStock, currentDateRange);
  };

  const navigateToPreviousStock = () => {
    if (watchlist.length === 0) return;
    
    const prevIndex = selectedStockIndex > 0 ? selectedStockIndex - 1 : watchlist.length - 1;
    const prevStock = watchlist[prevIndex];
    setSelectedStockIndex(prevIndex);
    handleStockSelect(prevStock, currentDateRange);
  };

  // Update selected stock index when stock is selected
  const handleStockSelectWithIndex = async (stock, dateRange = '1y') => {
    const index = watchlist.findIndex(item => item.symbol === stock.symbol);
    setSelectedStockIndex(index);
    handleStockSelect(stock, dateRange);
  };

  if (authLoading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
        <p>{t('loading')}</p>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Login onLogin={handleLogin} error={loginError} />;
  }

  return (
    <div className="App">
      <header className="app-header">
        <div className="container">
          <div className="header-content">
            <div className="header-title">
              <BarChart3 size={32} />
              <h1>{t('appTitle')}</h1>
            </div>
            <div className="header-actions">
              <div className="user-info">
                {t('welcome')}, {user?.username} ({user?.role})
              </div>
              <div className={`api-status ${apiStatus}`}>
                <div className={`status-dot ${apiStatus}`}></div>
                {t('apiStatus')} {t(apiStatus)}
              </div>
              <div className="header-button-row">
                <LanguageSelector />
                {user?.role === 'admin' && (
                  <button
                    onClick={() => setCurrentPage(currentPage === 'admin' ? 'stock' : 'admin')}
                    className="admin-toggle-btn"
                    title={currentPage === 'admin' ? 'Stock Dashboard' : 'Admin Panel'}
                  >
                    {currentPage === 'admin' ? 'Stock Dashboard' : 'Admin Panel'}
                  </button>
                )}
                {selectedStock && currentPage === 'stock' && (
                  <button
                    onClick={refreshData}
                    className="refresh-btn"
                    title={t('refresh')}
                  >
                    <RefreshCw size={16} />
                    <span>{t('refresh')}</span>
                  </button>
                )}
                <button
                  onClick={handleLogout}
                  className="logout-btn"
                  title={t('logout')}
                >
                  <LogOut size={16} />
                  <span>{t('logout')}</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </header>

      <main className="main-content">
        {apiStatus === 'unhealthy' && (
          <div className="alert alert-error">
            <div className="alert-content">
              <AlertCircle size={20} />
              <div>
                <div className="alert-title">{t('apiConnectionIssue')}</div>
                <div className="alert-message">
                  {t('apiConnectionMessage')}
                </div>
              </div>
            </div>
          </div>
        )}

        {currentPage === 'admin' ? (
          <AdminPage onNavigateToStock={() => setCurrentPage('stock')} />
        ) : (
          <div className="main-grid">
            <div className="sidebar">
              <StockSearch onStockSelect={handleStockSelectWithIndex} />
              <StockRecommendation 
                watchlist={watchlist}
                onStockSelect={handleStockSelectWithIndex}
              />
              <Portfolio 
                onStockSelect={handleStockSelectWithIndex} 
                onWatchlistChange={setWatchlist}
              />
            </div>

            <div className="content-area">
              {/* Mobile Navigation Arrows - Only visible on mobile */}
              {watchlist.length > 1 && selectedStock && (
                <div className="mobile-nav-arrows">
                  <button 
                    onClick={navigateToPreviousStock}
                    className="mobile-nav-arrow mobile-nav-up"
                    title={t('previousStock')}
                  >
                    <ChevronUp size={24} />
                  </button>
                  <div className="mobile-nav-indicator">
                    {selectedStockIndex + 1} / {watchlist.length}
                  </div>
                  <button 
                    onClick={navigateToNextStock}
                    className="mobile-nav-arrow mobile-nav-down"
                    title={t('nextStock')}
                  >
                    <ChevronDown size={24} />
                  </button>
                </div>
              )}
              
              <div ref={stockDetailsRef} className="stock-details-scroll-target">
                <StockQuote 
                  quote={quote} 
                  loading={loading.quote} 
                  error={error.quote}
                  selectedStock={selectedStock}
                />
              </div>
              <div className="charts-vertical">
                <StockChart 
                  data={historicalData} 
                  loading={loading.history} 
                  error={error.history}
                  symbol={selectedStock?.symbol}
                  onDateRangeChange={handleDateRangeChange}
                />
                <IntradayChart 
                  symbol={selectedStock?.symbol}
                  currentPrice={quote?.price}
                  quote={quote}
                />
              </div>
              <div className="analysis-grid">
                <StockAnalysis 
                  quote={quote}
                  historicalData={historicalData}
                  symbol={selectedStock?.symbol}
                />
                <AIAnalysis 
                  quote={quote}
                  historicalData={historicalData}
                  symbol={selectedStock?.symbol}
                />
              </div>
            </div>
          </div>
        )}
      </main>

      <footer className="footer">
        <div className="footer-content">
          <p className="footer-text">
            {t('footerText')}
          </p>
          <p className="footer-attribution">
            {t('footerAttribution')}
          </p>
        </div>
      </footer>
    </div>
  );
}

function App() {
  return (
    <LanguageProvider>
      <AppContent />
    </LanguageProvider>
  );
}

export default App;